// generatePackingList.js
import { PDFDocument, rgb } from "pdf-lib"
import fontkit from "@pdf-lib/fontkit"
import robotoMed from "../../../fonts/Roboto/Roboto-Medium.ttf"
import robotoReg from "../../../fonts/Roboto/Roboto-Regular.ttf"

import { db, storage } from "../../../firebase/config"
import { getDownloadURL, ref, uploadBytes } from "firebase/storage"
import { addDoc, collection, serverTimestamp } from "firebase/firestore"

/**
 * Generate a Packing List PDF and save its metadata to Firestore.
 *
 * @param {Object} data - The packing list data object.
 * @param {Object} translation - (Optional) Translation object with label overrides.
 * @returns {Promise<String>} downloadURL - The URL of the uploaded PDF.
 */
export const generatePackingList = async (data, translation = {}) => {
  // 1) Load fonts
  const robotoMediumBytes = await fetch(robotoMed).then((res) => res.arrayBuffer())
  const robotoRegularBytes = await fetch(robotoReg).then((res) => res.arrayBuffer())

  console.log("Packing List data:", data)

  // 2) Create a new PDF document and register fontkit
  const pdfDoc = await PDFDocument.create()
  pdfDoc.registerFontkit(fontkit)

  // 3) Embed the fonts
  const boldFont = await pdfDoc.embedFont(robotoMediumBytes)
  const regularFont = await pdfDoc.embedFont(robotoRegularBytes)

  // 4) Add a new A4 page
  const page = pdfDoc.addPage([595.28, 841.89])
  const { width } = page.getSize()

  // Basic layout settings
  const padding = 40
  const fontSize = 8
  let currentY = 800
  const textOffsetY = -4 // Slight upward offset for text

  // 5) Draw the "Packing List" Heading
  const packingListTitle = translation.packingListTitle || "PACKING LIST"
  const headingWidth = boldFont.widthOfTextAtSize(packingListTitle, 14)
  page.drawText(packingListTitle, {
    x: (width - headingWidth) / 2,
    y: currentY,
    size: 14,
    font: boldFont,
    color: rgb(0, 0, 0),
  })
  currentY -= 30

  // 6) Draw Packing List Number and Date
  const plNumberLabel = translation.plNumberLabel || "Packing List No.:"
  const dateLabel = translation.dateLabel || "Date:"
  const dateOptions = { year: "numeric", month: "long", day: "numeric" }
  const formattedDate = data.date
    ? data.date.toLocaleDateString("en-EN", dateOptions)
    : new Date().toLocaleDateString("en-EN", dateOptions)

  page.drawText(`${plNumberLabel} ${data.packingListNumber || "N/A"}`, {
    x: padding,
    y: currentY,
    size: fontSize,
    font: regularFont,
  })
  page.drawText(`${dateLabel} ${formattedDate}`, {
    x: padding + 250,
    y: currentY,
    size: fontSize,
    font: regularFont,
  })
  currentY -= 20

  // 7) Draw Sender and Receiver Info with labels on separate lines
  const senderInfoTitle = translation.senderTitle || "Sender Information:"
  const receiverInfoTitle = translation.receiverTitle || "Receiver Information:"

  const senderFields = [
    {
      label: translation.senderCompanyLabel || "Company:",
      value: data.sender?.senderCompany || "N/A",
    },
    {
      label: translation.senderRegLabel || "Registration:",
      value: data.sender?.senderReg || "N/A",
    },
    { label: translation.senderVatLabel || "VAT:", value: data.sender?.senderVat || "N/A" },
    {
      label: translation.senderLegalAddressLabel || "Legal Address:",
      value: data.sender?.senderLegalAddress || "N/A",
    },
    {
      label: translation.senderDeliveryAddressLabel || "Delivery Address:",
      value: data.sender?.senderDeliveryAddress || "N/A",
    },
  ]

  const receiverFields = [
    {
      label: translation.receiverCompanyLabel || "Company:",
      value: data.receiver?.receiverCompany || "N/A",
    },
    {
      label: translation.receiverRegLabel || "Registration:",
      value: data.receiver?.receiverReg || "N/A",
    },
    { label: translation.receiverVatLabel || "VAT:", value: data.receiver?.receiverVat || "N/A" },
    {
      label: translation.receiverLegalAddressLabel || "Legal Address:",
      value: data.receiver?.receiverLegalAddress || "N/A",
    },
    {
      label: translation.receiverDeliveryAddressLabel || "Delivery Address:",
      value: data.receiver?.receiverDeliveryAddress || "N/A",
    },
  ]

  // Draw sender title
  page.drawText(senderInfoTitle, {
    x: padding,
    y: currentY,
    size: fontSize,
    font: boldFont,
    color: rgb(0, 0, 0),
  })
  currentY -= 15

  // Draw each sender field on its own line
  senderFields.forEach((field) => {
    page.drawText(`${field.label} ${field.value}`, {
      x: padding + 10,
      y: currentY,
      size: fontSize,
      font: regularFont,
      color: rgb(0, 0, 0),
    })
    currentY -= 12
  })
  currentY -= 15

  // Draw receiver title
  page.drawText(receiverInfoTitle, {
    x: padding,
    y: currentY,
    size: fontSize,
    font: boldFont,
    color: rgb(0, 0, 0),
  })
  currentY -= 15

  // Draw each receiver field on its own line
  receiverFields.forEach((field) => {
    page.drawText(`${field.label} ${field.value}`, {
      x: padding + 10,
      y: currentY,
      size: fontSize,
      font: regularFont,
      color: rgb(0, 0, 0),
    })
    currentY -= 12
  })
  currentY -= 30

  // 8) Draw Table Header for Products
  const columns = [
    { label: translation.colNo || "#", width: 12 },
    { label: translation.colName || "Product", width: 400 },
    { label: translation.colQty || "Qty", width: 50 },
    { label: translation.colWeight || "Weight", width: 40 },
    { label: translation.colBoxes || "Boxes", width: 30 },
  ]
  let tableX = padding
  columns.forEach((col) => {
    page.drawText(col.label, {
      x: tableX + 2,
      y: currentY - textOffsetY,
      size: fontSize,
      font: boldFont,
    })
    tableX += col.width
  })
  currentY -= 15

  // 9) Draw each product row (filter out "New Plate" items)
  const filteredProducts = (data.products || []).filter((p) => p.name !== "New Plate")
  const tableStartY = currentY + 15
  const rowHeight = 15

  filteredProducts.forEach((item, index) => {
    let cellX = padding
    page.drawText(String(index + 1), {
      x: cellX + 2,
      y: currentY - textOffsetY,
      size: fontSize,
      font: regularFont,
    })
    cellX += columns[0].width
    page.drawText(String(item.name || ""), {
      x: cellX + 2,
      y: currentY - textOffsetY,
      size: fontSize,
      font: regularFont,
    })
    cellX += columns[1].width
    page.drawText(String(item.quantity || ""), {
      x: cellX + 2,
      y: currentY - textOffsetY,
      size: fontSize,
      font: regularFont,
    })
    cellX += columns[2].width
    page.drawText(String(item.weight || ""), {
      x: cellX + 2,
      y: currentY - textOffsetY,
      size: fontSize,
      font: regularFont,
    })
    cellX += columns[3].width
    page.drawText(String(item.boxesCount || ""), {
      x: cellX + 3,
      y: currentY - textOffsetY,
      size: fontSize,
      font: regularFont,
    })
    currentY -= rowHeight
  })

  // 10) Draw table borders
  const rowCount = filteredProducts.length
  const tableHeight = rowCount * rowHeight
  const tableBottomY = tableStartY - tableHeight
  const totalTableWidth = columns.reduce((sum, col) => sum + col.width, 0)

  for (let i = 0; i <= rowCount; i++) {
    const y = tableStartY - i * rowHeight
    page.drawLine({
      start: { x: padding, y },
      end: { x: padding + totalTableWidth, y },
      thickness: 0.5,
      color: rgb(0, 0, 0),
    })
  }
  let lineX = padding
  for (let c = 0; c < columns.length; c++) {
    page.drawLine({
      start: { x: lineX, y: tableStartY },
      end: { x: lineX, y: tableBottomY },
      thickness: 0.5,
      color: rgb(0, 0, 0),
    })
    lineX += columns[c].width
  }
  page.drawLine({
    start: { x: lineX, y: tableStartY },
    end: { x: lineX, y: tableBottomY },
    thickness: 0.5,
    color: rgb(0, 0, 0),
  })
  currentY = tableBottomY - 20

  // 11) Draw bottom comment if exists
  if (data.comment) {
    page.drawText(`${translation.commentLabel || "Comment:"}`, {
      x: padding,
      y: currentY,
      size: fontSize,
      font: boldFont,
    })
    currentY -= 15
    page.drawText(String(data.comment), {
      x: padding,
      y: currentY,
      size: fontSize,
      font: regularFont,
    })
    currentY -= 20
  }

  // 12) Save PDF and upload to Firebase Storage
  const pdfBytes = await pdfDoc.save()
  const fileName = data.packingListNumber || "PackingList"
  const pathToFile = `packingLists/${fileName}.pdf`

  const storageRef = ref(storage, pathToFile)
  await uploadBytes(storageRef, pdfBytes)
  const downloadURL = await getDownloadURL(storageRef)

  // 13) Save metadata to Firestore (optional)
  try {
    const packingListsRef = collection(db, "packingLists")
    await addDoc(packingListsRef, {
      packingListNumber: data.packingListNumber,
      created: serverTimestamp(),
      updated: serverTimestamp(),
      url: downloadURL,
      sender: {
        name: data.sender?.senderCompany || "",
        address: data.sender?.senderDeliveryAddress || "",
      },
      receiver: {
        name: data.receiver?.receiverCompany || "",
      },
      products:
        data.products
          ?.filter((p) => p.name !== "New Plate")
          .map((item) => ({
            name: item.name || "",
            quantity: item.quantity || 0,
            weight: item.weight || 0,
            boxesCount: item.boxesCount || 0,
          })) || [],
      comment: data.comment || "",
      invoiceUrl: data.invoiceUrl || null,
      userId: data.userId || null,
      clientId: data.clientId || null,
      manufacturerId: data.manufacturerId || null,
      status: "created",
    })
  } catch (error) {
    console.error("Error saving packing list to Firestore:", error)
  }

  return downloadURL
}
